import React, { useEffect, useRef } from "react"
import { FC, Key, ReactNode, useMemo, useState } from "react"

import {
  CopyOutlined,
  CloudSyncOutlined,
  DeleteOutlined,
  EditOutlined,
  FileExcelFilled,
  FilterFilled,
  PlusSquareOutlined,
  SettingOutlined
} from "@ant-design/icons"
import { Button, Popconfirm, Space, TablePaginationConfig, Tooltip, Flex, Card, Typography, TabsProps } from "antd"
import {
  ColumnsType,
  ExpandableConfig,
  FilterValue,
  SortOrder,
  SorterResult, TableCurrentDataSource,
  TableRowSelection
} from "antd/es/table/interface"
import { CheckboxProps } from "antd/lib"
import dayjs, { Dayjs } from "dayjs"

import variables from "@/_styles/variables.module.scss"
import { ColorifiedButton } from "@/components/_Shared/Button/ColorifiedButton"
import { FilterWrapper, FieldData } from "@/components/_Shared/FilterWrapper"
import { SimpleModal, SimpleModalRef } from "@/components/_Shared/SimpleModal"
import { ColumnsSetting } from "@/components/_Shared/Table/ColumnsSetting"
import { AuthorityProcessLog } from "@/components/Authorities/AuthorityProcessLog/AuthorityProcessLog"
import { Resource, Scope } from "@/constants/permission"
import { updateAuthoritiesFromSoap } from "@/http/authorities"

import classes from "./Table.module.scss"
import { CustomRangePicker } from "../CustomRangePicker/CustomRangePicker"
import { CustomTable } from "../CustomTable/CustomTable"
import { CustomTabs } from "../CustomTabs/CustomTabs"
import { SvgImage } from "../SvgImage/SvgImage"

import { useFilterStorage } from "@/hook/useFilterStorage"
import { usePermission } from "@/hook/usePermission"

import { BulkOrderStatus, BULK_ORDER_KEY, REQUEST_QUOTE_KEY, PERSONAL_OFFER_KEY } from "@/types/IBulkOrder"
import { OrderStatus, OrderStatusTabKeys } from "@/types/IOrder"
import { IFilterProps, ITableColumn } from "@/types/ui"

import { getNested } from "@/utils/getNested"

const { Title } = Typography

interface TablePaginationProps {
  currentPage?: number
  total?: number
  pageSize?: number
  onPageChange?: (page: number, pageSize: number) => void
  onPageSizeChange?: (current: number, size: number) => void
  showSizeChanger?: boolean
}

interface TableDownloadProps {
  onDownload: () => void
  isLoading: boolean
}

interface TablePermissionProps {
  resource: Resource
}

type ScrollConfig = {
  index?: number
  key?: Key
  top?: number
}
type OnCustomizeScroll = (info: {
  currentTarget?: HTMLElement
  scrollLeft?: number
}) => void

type CustomizeComponent = FC<any>
type CustomizeScrollBody<T> = (data: readonly T[], info: {
  scrollbarSize: number
  ref: React.Ref<{
      scrollLeft: number
      scrollTo?: (scrollConfig: ScrollConfig) => void
  }>
  onScroll: OnCustomizeScroll
}) => React.ReactNode

interface TableComponents<T> {
  table?: CustomizeComponent
  header?: {
      wrapper?: CustomizeComponent
      row?: CustomizeComponent
      cell?: CustomizeComponent
  }
  body?: CustomizeScrollBody<T> | {
      wrapper?: CustomizeComponent
      row?: CustomizeComponent
      cell?: CustomizeComponent
  }
}

type SizeType = "small" | "middle" | "large" | undefined

interface TableProps<T> {
  tableTitle?: string,
  tableTitleIcon?: ReactNode,
  tableTitleBtnPosition?: React.CSSProperties["justifyContent"],
  dataSource?: T[]
  columns: ITableColumn<T>[]
  onAdd?: () => void
  addText?: string
  onEdit?: (id: any) => void
  onCopy?: (selectedRowKeys: Key[]) => void
  onRemove?: (selectedRowKeys: Key[]) => void
  removeText?: string
  download?: TableDownloadProps
  onAction?: (selectedRowKeys: Key[]) => void
  actionText?: string
  /**
   * кастомный компонент для отображения вместо кнопок действия (onEdit, onCAdd и т.д.)
   */
  customActions?: ReactNode
  isActionLoading?: boolean
  isLoading?: boolean
  rowKey: string | string[]
  onRowClick?: (data: T, index: number | undefined) => void
  onUpdateColumns?: (newColumns: ITableColumn<T>[]) => void
  onSorterChange?: (value: string) => void
  pagination?: TablePaginationProps 
  isPagination?: boolean
  filters?: IFilterProps
  permission?: TablePermissionProps
  isSelectable?: boolean
  selectionCheckboxProps?: ((record: T) => Partial<Omit<CheckboxProps, "checked" | "defaultChecked">>) | undefined
  bordered?: boolean
  expandable?: ExpandableConfig<T>
  components?: TableComponents<T>
  footer?: (data: readonly T[]) => React.ReactNode
  summary?:(data: readonly T[]) => React.ReactNode,
  sortDirections?: SortOrder[],
  rowClassName?: (record: T, index: number | undefined) => string,
  className?: string,
  size?: SizeType,
  disabledCreateButton?: boolean
  statusTabs?: TabsProps
  hasDateFilter?: boolean
  headerOuter?: JSX.Element
  tabItemsFilterName?: string
  useModalForRemoveConfirm?: boolean
  syncAuthoritiesFromSoap?: boolean
  /**
   * Компонент для отображения между заголовком tableTitle и телом таблицы
   */
  middleComponent?: ReactNode
  showHeader?: boolean
}

export const Table = <T extends Object>(props: TableProps<T>) => {
  const {
    tableTitle = "",
    tableTitleIcon,
    tableTitleBtnPosition = "left",
    dataSource = [],
    columns,
    pagination,
    isPagination = true,
    onCopy,
    onRemove,
    onEdit,
    onAdd,
    onAction,
    addText = "Добавить",
    removeText = "Удалить",
    actionText,
    customActions,
    download,
    isLoading,
    isActionLoading,
    rowKey,
    onRowClick,
    onUpdateColumns,
    onSorterChange,
    filters,
    permission,
    isSelectable,
    selectionCheckboxProps,
    bordered,
    expandable,
    components,
    footer,
    summary,
    sortDirections,
    rowClassName,
    className,
    size,
    disabledCreateButton,
    statusTabs,
    hasDateFilter,
    headerOuter,
    tabItemsFilterName,
    useModalForRemoveConfirm,
    syncAuthoritiesFromSoap,
    middleComponent,
    showHeader
  } = props

  const {
    showSizeChanger,
    currentPage,
    pageSize,
    total,
    onPageChange,
    onPageSizeChange
  } = pagination || {}

  const visibleColumns = columns.filter((column) => column.isVisible) as ColumnsType<any>

  const { hasPermission } = usePermission()

  const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([])
  const [openFilters, setOpenFilters] = useState<boolean>(false)
  const [openSetting, setOpenSetting] = useState<boolean>(false)
  const [openSyncAuthoritiesFromSoap, setOpenSyncAuthoritiesFromSoap] = useState<boolean>(false)

  const simpleModalComponent = useRef<SimpleModalRef>(null)

  const onOpenFiltersHandler = () => {
    setOpenFilters(true)
  }

  const onOpenSettingHandler = () => {
    setOpenSetting(true)
  }

  const onCloseSettingHandler = () => {
    setOpenSetting(false)
  }

  const onSelectHandler = (newSelectedRowKeys: Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys)
  }

  const onCopyHandler = () => {
    if (selectedRowKeys.length && onCopy) {
      onCopy(selectedRowKeys)
    }
  }

  const onRemoveHandler = async () => {
    if (selectedRowKeys.length && onRemove) {
      await onRemove(selectedRowKeys)
      setSelectedRowKeys([])
    }
  }

  const onRemoveWithModalHandler = async () => {
    if (selectedRowKeys.length && onRemove) {
        simpleModalComponent.current?.open({
          title: "Предупреждение",
          text: "Вы действительно хотите удалить выбранные элементы?",
          ok: {
            text: "Удалить",
            action: async () => {
              await onRemove(selectedRowKeys)
              setSelectedRowKeys([])
              simpleModalComponent.current?.close()
            }
          },
          cancel: {
            text: "Отмена",
            action: () => {
              simpleModalComponent.current?.close()
            }
          }
        })
    }
  }

  const onEditHandler = () => {
    if(selectedRowKeys.length === 1 && onEdit) {
      const id = selectedRowKeys[0] as string
      onEdit(id)
    }
  }

  const onCreateHandler = () => {
    if (onAdd) {
      onAdd()
    }
  }

  const onActionHandler = async () => {
    if (selectedRowKeys.length && onAction) {
      await onAction(selectedRowKeys)
    }
  }

  const rowSelection: TableRowSelection<T> = {
    selectedRowKeys,
    onChange: onSelectHandler,
    getCheckboxProps: selectionCheckboxProps
  }

  const selection = isSelectable === false ? {} : {
    rowSelection: rowSelection
  }

  const onRow = (data: T, index?: number) => {
    return {
      onClick: (event: any) => {
        const rowCellWithLinkOrBtn = event.target.href !== undefined
          || event.target.type === "button"
          || event.target.parentElement.type === "button"
        return onRowClick && !rowCellWithLinkOrBtn ? onRowClick(data, index) : null
      }
    }
  }

  const onRowClassName = (data: T, index?: number): string => {
    return rowClassName ? `${classes.row} ${rowClassName(data, index)}` : `${classes.row}`
  }
  

  const onUpdatedColumnsHandler = (updatedColumns: ITableColumn<T>[]) => {
    if (onUpdateColumns) {
      onUpdateColumns(updatedColumns)
      setOpenSetting(false)
    }
  }

  const generateRowKey = (data: T): string => {
    if (Array.isArray(rowKey)) {
      return getNested(data, rowKey) || ""
    }

    return data[rowKey as keyof object]
  }

  const onSubmitSyncAuthoritiesFromSoapHandler = async () => {
      await updateAuthoritiesFromSoap()
  }

  const onOpenSyncAuthoritiesFromSoapHandler = () => {
    setOpenSyncAuthoritiesFromSoap(true)
  }

  const onCloseSyncAuthoritiesFromSoapHandler = () => {
    setOpenSyncAuthoritiesFromSoap(false)
  }

  const paginationProps: TablePaginationConfig = {
    position: ["bottomCenter"],
    current: currentPage,
    total,
    pageSize,
    showSizeChanger,
    onChange: onPageChange,
    onShowSizeChange: onPageSizeChange,
    showTotal: (total, range) => {return  <span>{range[0]}-{range[1]} из {total}</span>},
    showQuickJumper: true
  }

  const isCan = useMemo(() => {
    if(permission?.resource == null) return {
      Create: true,
      Update: true,
      Delete: true,
      Actions: true
    }

    return {
      Create: hasPermission(permission.resource, Scope.Create),
      Update: hasPermission(permission.resource, Scope.Update),
      Delete: hasPermission(permission.resource, Scope.Delete),
      Actions: hasPermission(permission.resource, Scope.Actions)
    }
  }, [hasPermission, permission])

  const showTableTitle = useMemo((): boolean => {
    let topContents: boolean = false
    let actionsContents: boolean = false

    topContents = !!(tableTitle || onUpdateColumns || download || filters)
    actionsContents = !!((isCan.Create && addText && onAdd)
    || (isCan.Update && onEdit)
    || (isCan.Delete && onRemove)
    || (isCan.Create && onCopy)
    || (isCan.Actions && actionText && onAction))

    return !!(topContents || actionsContents)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCan, tableTitle])

  const onTableChange = (
    _: TablePaginationConfig,
    __: Record<string, FilterValue | null>,
    sorter: SorterResult<T> | SorterResult<T>[],
    ___: TableCurrentDataSource<T>
  ) => {
    if (!onSorterChange) {
      return
    }


    if (!Array.isArray(sorter)) {
      if (!sorter.order) {
        onSorterChange("")
      } else {
        const order = sorter.order === "ascend" ? "asc" : "desc"

        const newOrderBy = `${(sorter.column as ITableColumn<T>)?.sorterField || sorter.columnKey} ${order}`
        onSorterChange(newOrderBy)
      }
    }
  }

  const [activeStatus, setActiveStatus] = useState<string>(OrderStatusTabKeys.All)
  const [activeDate, setActiveDate] = useState<string>("all")
  const [activeStartDate, setActiveStartDate] = useState<Dayjs | null>(null)
  const [activeEndDate, setActiveEndDate] = useState<Dayjs | null>(null)
  const [isResetFilter, setIsResetFilter] = useState<boolean>(false)

  const { saveUIState, getUIState } = useFilterStorage({ key: filters?.filterKey ?? "" })
  const [isRestored, setIsRestored] = useState(false)

  useEffect(() => {
    const saved = getUIState()
    if (saved) {
      setActiveDate(saved.activeDate || "all")
      setActiveStartDate(saved.activeStartDate ? dayjs(saved.activeStartDate) : null)
      setActiveEndDate(saved.activeEndDate ? dayjs(saved.activeEndDate) : null)
    }
    if (saved?.activeStatus) {
      setActiveStatus(saved.activeStatus)
    } else if (statusTabs?.defaultActiveKey) {
      setActiveStatus(statusTabs.defaultActiveKey)
    }
    setIsRestored(true)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!isRestored) return
    saveUIState({ activeDate, activeStartDate, activeEndDate, activeStatus })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRestored, activeDate, activeStartDate, activeEndDate, activeStatus, activeStatus])

  const onRangeChange = (dates: any) => {
    if (!dates) {
      setActiveStartDate(null)
      setActiveEndDate(null)

      return
    }

    setActiveStartDate(dates[0])
    setActiveEndDate(dates[1])
    setActiveDate("period")
  }

  const generateExternalFilters = useMemo(() => {
    if (!statusTabs && !hasDateFilter) return

    const externalFilters: FieldData[] = []
    
    if (activeStatus && tabItemsFilterName) {
      if (activeStatus === "All") {
          externalFilters.push({
          name: [tabItemsFilterName],
          value: null
        })
      } else {
        externalFilters.push({
          name: [tabItemsFilterName],
          value: [
            activeStatus
          ]
        })
      }
    }

    if (activeStatus && !tabItemsFilterName) {
      switch (activeStatus) {
        case OrderStatusTabKeys.OnApproval:
          externalFilters.push({
            name: [tabItemsFilterName ?? "Status"],
            value: [
              OrderStatus.New,
              OrderStatus.IsReserve, 
              OrderStatus.Placed,
              OrderStatus.DispatcherTimeCoordination,
              OrderStatus.ClientTimeCoordination,
              OrderStatus.OnApproval,
              OrderStatus.OnApprovalDelivery,
              OrderStatus.OnClientApprovingTransport
            ]
          })
          break
        case OrderStatusTabKeys.WaitingForPayment:
          externalFilters.push({
            name: [tabItemsFilterName ?? "Status"],
            value: [
              OrderStatus.WaitingForPayment,
              OrderStatus.TimeIsSet
            ]
          })
          break
        case OrderStatusTabKeys.Approved:
          externalFilters.push({
            name: [tabItemsFilterName ?? "Status"],
            value: [ 
              OrderStatus.Approved,
              OrderStatus.OnLoading
             ]
          })
          break
        case OrderStatusTabKeys.Shipped:
          externalFilters.push({
            name: [tabItemsFilterName ?? "Status"],
            value: [
              OrderStatus.Shipped,
              OrderStatus.Closed
            ]
          })
          break
        case OrderStatusTabKeys.Canceled:
          externalFilters.push({
            name: [tabItemsFilterName ?? "Status"],
            value: [
              OrderStatus.Canceled,
              OrderStatus.NotSettedTime
            ]
          })
          break
        case OrderStatusTabKeys.InProcess:
          externalFilters.push({
            name: [tabItemsFilterName ?? "Status"],
            value: [
              BulkOrderStatus.Accept
            ]
          })
          break
        case OrderStatusTabKeys.Active:
          externalFilters.push({
            name: [tabItemsFilterName ?? "Status"],
            value: [
              BulkOrderStatus.InProcess,
              BulkOrderStatus.CurrentOffer
            ]
          })
          break
        case OrderStatusTabKeys.Archive:
          if(filters?.filterKey === REQUEST_QUOTE_KEY || filters?.filterKey === PERSONAL_OFFER_KEY) {
            externalFilters.push({
              name: [tabItemsFilterName ?? "Status"],
              value: [
                BulkOrderStatus.Accept,
                BulkOrderStatus.Canceled,
                BulkOrderStatus.Closed
              ]
            })
          } else if(filters?.filterKey === BULK_ORDER_KEY) {
            externalFilters.push({
              name: [tabItemsFilterName ?? "Status"],
              value: [
                BulkOrderStatus.Closed,
                BulkOrderStatus.Canceled
              ]
            })
          }
          break
        default:
          externalFilters.push({
            name: [tabItemsFilterName ?? "Status"],
            value: null
          })
          break
      }
    }

    if (activeDate === "all") {
      setActiveStartDate(null)
      setActiveEndDate(null)
      externalFilters.push({
        name: ["Date"],
        value: null
      })
    }

    if (activeDate === "today") {
      setActiveStartDate(null)
      setActiveEndDate(null)
      externalFilters.push({
        name: ["Date"],
        value: [dayjs().startOf("day"), dayjs().endOf("day")]
      })
    }

    if (activeDate === "period") {
      externalFilters.push({
        name: ["Date"],
        value: (activeStartDate && activeEndDate) 
          ? [dayjs(activeStartDate).startOf("day"), dayjs(activeEndDate).endOf("day")]
          : null
      })
    }
    return externalFilters
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStatus, activeDate, activeStartDate, activeEndDate, isResetFilter])


  return (
    <>
      <Card hidden={!showTableTitle} className={classes.titleCard} bordered={false}
          title={tableTitle && (
            <Title level={3} className={classes.titleText}>
              <Space size={12}>
                <div style={{ color: variables.secondBasecolor }}>
                  {tableTitleIcon ?? <SvgImage src="/images/table-icon.svg" width={24} height={19.2} />}
                </div>
                {tableTitle}
              </Space>
            </Title>
          )}
          extra={<Space>
            {
              syncAuthoritiesFromSoap && (
                <Tooltip title="Получить данные по доверенностям из 1C">
                  <Button
                    color="primary" variant="outlined"
                    className={classes.button}
                    onClick={onOpenSyncAuthoritiesFromSoapHandler}
                    icon={<CloudSyncOutlined />}
                  />
                </Tooltip>
              )
            }
            {onUpdateColumns && (
              <Tooltip title="Настройка полей">
                <Button
                  color="primary" variant="outlined"
                  icon={<SettingOutlined />}
                  className={classes.button}
                  onClick={onOpenSettingHandler}
                />
              </Tooltip>
            )}
            {
              download && (
                <Tooltip title="Выгрузить в Excel">
                  <Button
                    color="primary" variant="outlined"
                    disabled={download.isLoading || isActionLoading}
                    className={classes.button}
                    onClick={download.onDownload}
                    icon={<FileExcelFilled />}
                  >
                    Экспорт XLS
                  </Button>
                </Tooltip>
              )
            }
            {filters && !hasDateFilter && (
              <Tooltip title="Фильтр">
                <Button
                  type="primary"
                  icon={<FilterFilled />}
                  className={classes.button}
                  style={{ color: variables.baseBgColorContent }}
                  onClick={onOpenFiltersHandler}>Фильтр</Button>
              </Tooltip>
            )}
          </Space>}
      >

        <Flex justify={tableTitleBtnPosition}>
          <Space>
            {
              isCan.Create && addText && onAdd && (
                <ColorifiedButton
                  themecolor="orange"
                  disabled={disabledCreateButton}
                  onClick={onCreateHandler}
                  type="primary"
                  icon={<PlusSquareOutlined />}>
                  {addText}
                </ColorifiedButton>
              )
            }
            {
              isCan.Update && onEdit && (
                <Tooltip title="Редактировать">
                  <Button disabled={selectedRowKeys.length !== 1 || isActionLoading} onClick={onEditHandler} color="primary" variant="dashed" icon={<EditOutlined />} />
                </Tooltip>
              )
            }
            {
              isCan.Delete && onRemove && (
                useModalForRemoveConfirm
                  ? <>
                    <SimpleModal ref={simpleModalComponent}/>
                    <Tooltip title={removeText}>
                      <Button
                        onClick={onRemoveWithModalHandler}
                        disabled={!selectedRowKeys.length || isActionLoading}
                        color="primary" variant="dashed"
                        icon={<DeleteOutlined/>}/>
                    </Tooltip>
                    </>
                  : <Popconfirm disabled={!selectedRowKeys.length || isActionLoading}
                                onConfirm={onRemoveHandler}
                                title={removeText}
                                description={`Вы действительно хотите ${removeText.toLocaleLowerCase()}?`}
                                okText="Да"
                                cancelText="Нет">
                      <Tooltip title={removeText}>
                        <Button
                          disabled={!selectedRowKeys.length || isActionLoading}
                          color="primary" variant="dashed"
                          icon={<DeleteOutlined/>}/>
                      </Tooltip>
                    </Popconfirm>
              )
            }
            {
              isCan.Create && onCopy && (
                <Tooltip title="Скопировать">
                  <Button
                    disabled={selectedRowKeys.length !== 1 || isActionLoading}
                    onClick={onCopyHandler}
                    color="primary" variant="dashed"
                    icon={<CopyOutlined/>}/>
                </Tooltip>
              )
            }
            {
                isCan.Actions && actionText && onAction && (
                    <Button
                        disabled={selectedRowKeys.length === 0 || isActionLoading}
                        onClick={onActionHandler}
                        type="primary">
                      {actionText}
                    </Button>
                )
            }
            {
              customActions
            }
          </Space>
        </Flex>
      </Card>

      {
        headerOuter &&
          <Flex className={classes["outerHeader"]}>
            {
              headerOuter
            }
          </Flex>
      }
      
      {
        middleComponent
      }

      {statusTabs &&
        <CustomTabs
          type="card"
          {...statusTabs}
          activeKey={activeStatus}
          onChange={setActiveStatus} 
          style={{ marginBottom: "-17px" }}
        />
      }

      <Card className={statusTabs ? classes.tableCardWithTabs : classes.tableCard}>
        {hasDateFilter && 
          <>
            <Flex justify="space-between" className={classes.actionsWithDate}>
              <Flex gap="middle">
                <Button
                  type={activeDate === "all" ? "primary" : "default"}
                  style={{ 
                    borderColor: activeDate === "all" ? variables.secondBasecolor : variables.basecolorlight,
                    color: activeDate === "all" ? variables.baseBgColorContent : variables.basecolorlight
                  }}
                  shape="round"
                  onClick={() => setActiveDate("all")}
                >
                  Все дни
                </Button>
                <Button
                  type={activeDate === "today" ? "primary" : "default"}
                  style={{ 
                    borderColor: activeDate === "today" ? variables.secondBasecolor : variables.basecolorlight,
                    color: activeDate === "today" ? variables.baseBgColorContent : variables.basecolorlight
                  }}
                  shape="round"
                  onClick={() => setActiveDate("today")}
                >
                  Сегодня
                </Button>
                <Button
                  type={activeDate === "period" ? "primary" : "default"}
                  style={{ 
                    borderColor: activeDate === "period" ? variables.secondBasecolor : variables.basecolorlight,
                    color: activeDate === "period" ? variables.baseBgColorContent : variables.basecolorlight
                  }}
                  shape="round"
                  onClick={() => setActiveDate("period")}
                >
                  Выбрать период
                </Button>
                {activeDate === "period" &&
                  <CustomRangePicker
                    value={[activeStartDate ? dayjs(activeStartDate) : undefined, activeEndDate ? dayjs(activeEndDate) : undefined]}
                    onChange={onRangeChange}
                  />
                }
              </Flex>
              {filters && 
                <Flex>
                  <Tooltip title="Фильтр">
                    <Button
                      type="default" variant="outlined"
                      style={{ borderColor: variables.basecolorlight }}
                      icon={<SvgImage src="images/filter-icon.svg" width={16} height={12} />}
                      onClick={onOpenFiltersHandler}
                    />
                  </Tooltip>
                </Flex>
              }
            </Flex>
          </>
        }

        <CustomTable
          size={size}
          components={components}
          locale={{ emptyText: "Нет данных" }}
          columns={visibleColumns}
          dataSource={dataSource}
          showSorterTooltip={true}
          showHeader={showHeader}
          bordered={bordered}
          {...selection}
          rowKey={generateRowKey}
          loading={isLoading}
          onRow={onRow}
          rowClassName={onRowClassName}
          pagination={isPagination ? { ...paginationProps, showSizeChanger: !!onPageSizeChange } : false}
          onChange={onTableChange}
          expandable={expandable}
          footer={footer}
          summary={summary}
          sortDirections={sortDirections}
          className={className}          
        />
      </Card>

      {onUpdateColumns && (
        <ColumnsSetting
          open={openSetting}
          onCancel={onCloseSettingHandler}
          columns={columns}
          onSubmit={onUpdatedColumnsHandler}
        />
      )}
      {filters && (
        <FilterWrapper
          filterKey={filters.filterKey}
          open={openFilters}
          setOpen={setOpenFilters}
          filters={filters.filters}
          submitText={filters.submitText}
          cancelText={filters.cancelText}
          onSubmit={filters.onSubmit}
          onCancel={() =>{
            if(filters.onCancel){
              filters.onCancel()
              setIsResetFilter(!isResetFilter)
            }
          }}
          externalFilters={generateExternalFilters}
        />
      )}
      {syncAuthoritiesFromSoap && (
        <AuthorityProcessLog
          open={openSyncAuthoritiesFromSoap}
          onCancel={onCloseSyncAuthoritiesFromSoapHandler}
          onSubmit={onSubmitSyncAuthoritiesFromSoapHandler}
        />
      )}
    </>
  )
}